
import { splitLineup, buildLineupEntries } from "@ae/lineup";
import buttonActionMap from "./buttonActionMap";
import {
    getEventName,
    getStaffFullName,
} from "./playerName";

export const buttons = Object.entries(buttonActionMap).map(([id, action]) => ({
    id,
    label: action.replace(/_/g, " "),
    action
}));

export const buttonModes = {
    F3: "latch",
};

const normalizeLineupRole = (rawRole, hasPlayerRecord) => {
    if (typeof rawRole === "string" && rawRole.trim().toLowerCase() === "player") {
        return "Player";
    }
    if (!rawRole && hasPlayerRecord) {
        return "Player";
    }
    return rawRole || null;
};

// Broad-truthy parser for boolean-ish flags coming off DDS records. The
// is_goalkeeper / is_captain fields arrive variously as booleans or the
// strings "yes"/"true" depending on the writer (aether vs opta vs operator).
/**
 * Running per-team red card count derived from the event list.
 * red_card and double_yellow both contribute 1. Used to populate
 * Team1.iRedCardTabs / Team2.iRedCardTabs on the clock graphic.
 */
// AeDrive-authoritative per-team yellow/red counts derived from the event
// list. Feeds the totalYellowCardOpus / totalRedCardOpus team_stat rows.
// double_yellow counts toward BOTH (a 2nd yellow is also a sending-off).
// Accepts the inconsistent event_key spellings present in the codebase.
export const computeOpusCardCounts = (events = [], teamId) => {
    let yellow = 0;
    let red = 0;
    for (const ev of events) {
        if (ev?.team_id !== teamId) continue;
        const key = (ev?.event_key || '').toLowerCase();
        if (key === 'yellow_card') yellow += 1;
        else if (key === 'red_card') red += 1;
        else if (key === 'double_yellow') {
            yellow += 1;
            red += 1;
        }
    }
    return { yellow, red };
};

// Team scores derived from the goal events on the timeline. The goal plugin
// (goal_event_object_builder.js) stores own-goal events with team_id already
// flipped to the scoring (benefiting) team, so we count by event.team_id
// directly — no further flip needed here. Keeps the helper symmetric with
// computeOpusCardCounts and avoids drift from incremental ±1 patches.
const GOAL_EVENT_KEYS = new Set(['goal', 'penalty', 'own_goal']);

// Clock-transition event keys → human label shown on the timeline divider.
// All period markers show the real wall-clock time of day they were triggered
// (see eventIdToLocalTime), rendered in each viewer's own local timezone.
const PERIOD_LABELS = {
    clock_start_1h: 'FIRST HALF · KICK OFF',
    clock_end_1h: 'FIRST HALF ENDED',
    clock_start_2h: 'SECOND HALF · KICK OFF',
    clock_end_2h: 'SECOND HALF ENDED',
};

// Every event_id is "<event_key>_<Date.now()>", so the trailing epoch (ms) is
// the absolute instant the event was triggered. Render it as 24-hour HH:MM:SS
// in the viewer's local timezone (getHours/Minutes/Seconds are local), so an
// event triggered at 10:44 IST shows as 06:14:00 to a London viewer. Returns
// null when the id carries no epoch suffix.
const eventIdToLocalTime = (eventId) => {
    const m = String(eventId ?? '').match(/_(\d{10,})$/);
    if (!m) return null;
    const d = new Date(Number(m[1]));
    if (Number.isNaN(d.getTime())) return null;
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
};

export const computeOpusGoalScores = (events = [], homeTeamId, awayTeamId) => {
    let home = 0;
    let away = 0;
    for (const ev of events) {
        const key = (ev?.event_key || '').toLowerCase();
        if (!GOAL_EVENT_KEYS.has(key)) continue;
        if (ev?.team_id === homeTeamId) home += 1;
        else if (ev?.team_id === awayTeamId) away += 1;
    }
    return { home_team_score: home, away_team_score: away };
};

/** A team's most recent goal event (by match time), or null if it has none. */
export const findLatestTeamGoal = (events = [], teamId) => {
    if (!teamId) return null;
    return (events || [])
        .filter((e) => GOAL_EVENT_KEYS.has(String(e?.event_key || '').toLowerCase()) && e.team_id === teamId)
        .reduce(
            (acc, cur) => ((cur?.time_in_seconds ?? -Infinity) >= (acc?.time_in_seconds ?? -Infinity) ? cur : acc),
            null,
        );
};

/**
 * Goalscorers for one team, shaped for the scoreline graphic's Scorers block
 * (gfx_LF_Scoreline Team1/Team2.Scorers.PlayerArray). The event-service
 * clock_end_1h / clock_end_2h builders already accept this array as
 * payload.homeScorers / payload.awayScorers and expand it to Player1..Player8 —
 * aedrive just has to supply it, since the builders run sandboxed and can't
 * read the timeline themselves.
 *
 * Names resolve through playerLookup rather than the stored scorer* fields, so
 * goals logged before the scorer name travelled with the event still render.
 * The lookup spans both squads on purpose: own goals are credited to the
 * BENEFITING team's team_id (see computeOpusGoalScores above) while the scorer
 * belongs to the other squad.
 *
 * A player who scored more than once collapses to one entry with the minutes
 * joined, matching the on-air format: { strSecondName: 'Brobbey',
 * strMinutes: "6', 23'" }. Own goals are annotated "7' (OG)".
 *
 * Goals whose player can't be resolved (deleted from the squad) are skipped
 * rather than rendered as a blank row — the builder derives iHowMany from the
 * array length, so a blank entry would leave a gap on air.
 */
export const buildScorersByTeam = (events = [], playerLookup, teamId) => {
    if (!teamId) return [];

    // Chronological, so a repeat scorer is ordered by their FIRST goal and the
    // joined minutes read in order. DDS doesn't guarantee timeline order.
    const goals = (events || [])
        .filter((ev) => GOAL_EVENT_KEYS.has((ev?.event_key || '').toLowerCase()))
        .filter((ev) => ev?.team_id === teamId)
        .sort((a, b) => (Number(a.time_in_seconds) || 0) - (Number(b.time_in_seconds) || 0));

    const order = [];
    const minutesByName = new Map();
    const seenEventIds = new Set();

    for (const ev of goals) {
        // Subscription echoes can deliver the same row twice; a duplicate would
        // show as a repeated minute against the scorer.
        if (ev.event_id) {
            if (seenEventIds.has(ev.event_id)) continue;
            seenEventIds.add(ev.event_id);
        }

        const name = getEventName(ev.player1_id ? playerLookup?.get(ev.player1_id) : null);
        if (!name) continue;

        const isOwnGoal = (ev.event_key || '').toLowerCase() === 'own_goal';
        // One second past the minute counts as the next minute: a goal at 12:01
        // reads 13'. Must stay ceil() to match the goalscorer graphic, which
        // rounds the same way from raw seconds in n8n's Build Payload for
        // Combined — the two graphics show the same goal and must agree.
        // Not the same as the mm:ss timeline clock below, which stays floor().
        const minute = `${Math.ceil((Number(ev.time_in_seconds) || 0) / 60)}'${isOwnGoal ? ' (OG)' : ''}`;

        if (minutesByName.has(name)) {
            minutesByName.get(name).push(minute);
        } else {
            minutesByName.set(name, [minute]);
            order.push(name);
        }
    }

    // The graphic has eight scorer slots; the builder also slices to 8.
    return order.slice(0, 8).map((name) => ({
        strSecondName: name,
        strMinutes: minutesByName.get(name).join(', '),
    }));
};

export const computeRedCardTabs = (events = [], homeTeamId, awayTeamId) => {
    let home = 0;
    let away = 0;
    for (const ev of events) {
        const key = (ev?.event_key || "").toLowerCase();
        if (key !== "red_card" && key !== "double_yellow") continue;
        if (ev.team_id === homeTeamId) home += 1;
        else if (ev.team_id === awayTeamId) away += 1;
    }
    return { home, away };
};

export const buildLineup = (team, events = []) => {
    if (!team) return { players: [], subs: [], staff: [] };

    const playerMap = new Map(
        (team.player || []).map((p) => [p.player_id, p])
    );

    // Seed dynamic formation places from the base lineup.
    const dynamicFormationPlace = new Map();
    (team.lineup || []).forEach(l => {
        if (l.formation_place != null && l.formation_place !== '') {
            dynamicFormationPlace.set(l.lineup_id, l.formation_place);
        }
    });

    // Replay substitutions chronologically to transfer formation_place.
    // concussion_sub is a distinct event_key (see sub_event_object_builder.js)
    // but is still a substitution for lineup-replay purposes.
    const subEvents = (events || [])
        .filter(e => ['sub', 'concussion_sub'].includes(e.event_key?.toLowerCase()))
        .sort((a, b) => (Number(a.time_in_seconds) || 0) - (Number(b.time_in_seconds) || 0));

    // Initial starter/sub classification via the SHARED @ae/lineup splitLineup
    // rule (formation_place 1–11 is canonical) so aedrive and aether agree on who
    // is in the XI — neither app keeps its own divergent rule. The substitution
    // replay below then moves subbed-off starters to the bench.
    //
    // Cold-start is disabled here on purpose: aedrive's own signal-less fallback
    // (further down) handles that case AND seeds dynamicFormationPlace, which
    // splitLineup doesn't know about. Player rows only (staff/orphan excluded);
    // player_position falls back to the squad's natural position for legacy
    // payloads with no per-row position.
    const onPitchSet = new Set();
    const classifiableRows = (team.lineup || [])
        .filter((l) => normalizeLineupRole(l.role, playerMap.has(l.lineup_id)) === 'Player')
        .map((l) => ({
            ...l,
            id: l.lineup_id,
            player_position: l.player_position || playerMap.get(l.lineup_id)?.player_position,
        }));
    splitLineup(classifiableRows, { coldStartFallback: false }).players.forEach(
        (p) => onPitchSet.add(p.id),
    );

    // Every player who has ever gone off (player1_id on any sub /
    // concussion_sub event), regardless of whether they started the match or
    // came on themselves as a substitute. A player can only leave the pitch
    // once per the laws of the game, so this set is exactly "permanently
    // ineligible" — including a substitute who was later subbed off again
    // (a case @ae/lineup's own is_starting-vs-isOnPitch mismatch check can't
    // see, since a bench player's is_starting is false both before coming on
    // and after going off again).
    const subbedOffEverSet = new Set();
    subEvents.forEach(event => {
        const pOff = event.player1_id;
        const pOn = event.player2_id;
        if (pOff && pOn) {
            subbedOffEverSet.add(pOff);
            const place = dynamicFormationPlace.get(pOff);
            if (place) {
                dynamicFormationPlace.set(pOn, place);
                dynamicFormationPlace.delete(pOff); // Subbed-off player yields their slot
            }
            if (onPitchSet.has(pOff)) {
                onPitchSet.delete(pOff);
                onPitchSet.add(pOn);
            }
        }
    });

    // Cold-start fallback (Phase 6): every priority rule failed and no
    // player row got added to onPitchSet. This is the "Pull Lineup landed
    // but no starter signal was published" case (aether's pull sometimes
    // leaves rows with formation_place=null, is_starting=undefined, and
    // player_position=null). Mirror aether's own local convention of
    // treating the first N lineup player-rows (array order) as
    // formation_place 1..N starters. Capped at 11.
    //
    // Guarded carefully so it ONLY fires when EVERY signal is absent. If
    // any row has any of: is_starting (true/false), any formation_place,
    // or an explicit player_position — aether has spoken about the lineup
    // shape and we honour that, even if the result is 0 starters.
    if (onPitchSet.size === 0) {
        const playerRows = (team.lineup || []).filter(
            (l) => normalizeLineupRole(l.role, playerMap.has(l.lineup_id)) === 'Player',
        );
        const everyRowSignalLess = playerRows.every((l) => (
            l.is_starting !== true && l.is_starting !== false
            && (l.formation_place == null || l.formation_place === '')
            && (l.player_position == null || l.player_position === '')
        ));
        if (everyRowSignalLess && playerRows.length > 0) {
            playerRows.slice(0, 11).forEach((l, index) => {
                onPitchSet.add(l.lineup_id);
                if (!dynamicFormationPlace.has(l.lineup_id)) {
                    dynamicFormationPlace.set(l.lineup_id, index + 1);
                }
            });
        }
    }

    // Build goal maps from events (dedupe by event_id to avoid double-counting).
    // Mirrors cardMap below: keyed by scorer (player1_id). own_goal is tallied
    // SEPARATELY from goal/penalty — the player who put it in their own net
    // didn't score for their own team, so their row badges "OG", not a count.
    // (Team-level scoring in computeOpusGoalScores already flips own_goal's
    // team_id to the benefiting side; that logic is untouched here.)
    const goalMap = new Map();
    const ownGoalMap = new Map();
    const seenGoalEventIds = new Set();

    (events || []).forEach(event => {
        const eventType = event.event_key?.toLowerCase();
        const playerId = event.player1_id;

        if (playerId && GOAL_EVENT_KEYS.has(eventType)) {
            const goalEventId = event.event_id || `goal-${playerId}-${eventType}-${event.updatedAt || event.updated_at || ''}`;
            if (seenGoalEventIds.has(goalEventId)) return;
            seenGoalEventIds.add(goalEventId);

            const targetMap = eventType === 'own_goal' ? ownGoalMap : goalMap;
            targetMap.set(playerId, (targetMap.get(playerId) || 0) + 1);
        }
    });

    // Build card map from events (dedupe by event_id to avoid double-counting)
    const cardMap = new Map();
    const seenCardEventIds = new Set();

    (events || []).forEach(event => {
        const eventType = event.event_key?.toLowerCase();
        const playerId = event.player1_id;

        if (playerId && (eventType === 'yellow_card' || eventType === 'red_card' || eventType === 'double_yellow')) {
            const cardEventId = event.event_id || `card-${playerId}-${eventType}-${event.updatedAt || event.updated_at || ''}`;
            if (seenCardEventIds.has(cardEventId)) return;
            seenCardEventIds.add(cardEventId);

            if (!cardMap.has(playerId)) {
                cardMap.set(playerId, { yellow: 0, red: 0 });
            }
            const cards = cardMap.get(playerId);
            if (eventType === 'yellow_card') {
                // @ae/lineup lights yellow2 (2nd-yellow/sent-off) purely off
                // cards.yellow >= 2 — it can't tell a genuine double_yellow
                // apart from two duplicate yellow_card events (which the
                // "Possible duplicate" confirm dialog's Continue button
                // deliberately allows logging). Capping at 1 here means only
                // an actual double_yellow event below can ever push a player
                // into the sent-off badge state.
                cards.yellow = Math.max(cards.yellow, 1);
            } else if (eventType === 'double_yellow') {
                cards.yellow = 2;
                cards.red = 1;
            } else if (eventType === 'red_card') {
                cards.red += 1;
            }
        }
    });

    // ---- Shared entry build (@ae/lineup) ----
    // buildLineupEntries owns the captain / goalkeeper-glove / starter-split /
    // orphan rules so AeDrive and Aether render IDENTICAL players + subs from the
    // same DDS data. This replaced AeDrive's own buildPlayerData, whose is_captain
    // OR-chain rendered a 2nd armband (it OR-ed the squad record's flag onto the
    // lineup row) and whose glove resolution diverged from Aether. AeDrive's two
    // match-specific signals ride in as builder options: onPitchIds +
    // formationPlaceById (the substitution replay above) and the card counts (via
    // enrichRow). includeOthers:false — TXLive has no squad box (XI + bench +
    // COACH only). includeOrphans:false — drop ghost rows for players deleted from
    // the squad (no "0 / —" stub), matching the previous orphan filter.
    const { players, subs } = buildLineupEntries(
        team.player,
        team.lineup,
        {
            onPitchIds: onPitchSet,
            formationPlaceById: dynamicFormationPlace,
            includeOthers: false,
            includeOrphans: false,
            enrichRow: (entry) => ({
                ...entry,
                cards: cardMap.get(entry.id) || { yellow: 0, red: 0 },
                goals: goalMap.get(entry.id) || 0,
                ownGoals: ownGoalMap.get(entry.id) || 0,
                isOnPitch: onPitchSet.has(entry.id),
                isStarter: onPitchSet.has(entry.id),
                // Forces @ae/lineup's shouldShowSubIcon mismatch check (which only
                // compares is_starting against the CURRENT isOnPitch) to flag a
                // substitute who was subbed off a second time — their own
                // is_starting is false both before and after, so without this
                // override the mismatch never fires and they resort to the top of
                // the subs list as if untouched. Only ever applied to rows that are
                // already permanently off (subbedOffEverSet), so it can't affect a
                // player currently on the pitch.
                ...(subbedOffEverSet.has(entry.id) ? { is_starting: true } : {}),
            }),
        },
    );

    // Extract staff (coaches, managers, etc.).
    // Staff records live in team.staff[] — NOT in team.player[] — so we have
    // to look up the lineup_id there. Without this lookup the staff rows
    // came back blank because playerMap never contained them, and the COACH
    // panel at the bottom of the team UI rendered an empty name.
    const staffMap = new Map(
        (team.staff || []).map((s) => [s.staff_id, s]),
    );
    const staff = (team.lineup || [])
        .filter((l) => normalizeLineupRole(l.role, false) === "Staff" || (l.role || "").toLowerCase() === "staff")
        .map((l) => {
            const id = l.lineup_id;
            const s = staffMap.get(id) || {};
            return {
                id,
                role: "Staff",
                staff_role: l.staff_role || s.staff_role || null,
                // OPUS-1269: carry the real DB title through so @ae/lineup can show
                // "Manager / Head Coach / …" instead of a hardcoded "Coach".
                staff_title: s.staff_title || null,
                staff_id: id,
                staff_first_name: s.staff_first_name,
                staff_last_name: s.staff_last_name,
                staff_short_name: s.staff_short_name,
                // Backwards-compat alias so consumers that still read
                // player_last_name on a staff row don't blow up.
                player_last_name: s.staff_last_name,
                player_short_name: s.staff_short_name,
                player_name: getStaffFullName(s),
            };
        });

    return { players, subs, staff };
};

// Sticky-last-good selector for buildLineup output. Aether's bulk Pull
// Lineup streams one row at a time; between emits, a team's lineup can
// briefly carry rows with no starter signal (the rows that haven't been
// patched yet). That makes buildLineup return { players: [], subs: [N] }
// for a beat, which renders as the panel regressing from 11/9 back to
// "all in subs". This helper holds the previous valid split when the next
// projection is "degenerate" (zero starters but non-empty subs) so the
// transient frame is never user-visible.
//
// The helper is intentionally pure so it's importable from tests. The
// caller (TXLive) owns the per-team cache.
export const pickStickyLineup = (prev, next) => {
    if (!next) return prev || null;
    const degenerate = next.players?.length === 0 && next.subs?.length > 0;
    if (degenerate && prev && prev.players?.length > 0) return prev;
    return next;
};

export const actionToSection = (action) => {
    if (!action) return null;
    if (action.includes("TEAM_1") || action.includes("TEAM_2")) return "Main";
    const map = {
        ADDITIONAL_TIME: "AdditionalTime",
        ATTACKING_THREAT: "AttackingThreat",
        CARD: "Cards",
        GOALSCORER: "GoalScorer",
        MATCH_STATS: "MatchStats",
        FREE_TEXT: "FreeText",
        SPONSOR: "Sponsor",
        SUBSTITUTION: "Substitution",
        WIN_PROBABILITY: "WinProbability",
        TEAM_NAMES: "Main",
    };
    const root = action.replace(/_(IN|OUT|PREVIEW|UPDATE|CONTINUE)$/, "");
    return map[root] || null;
};

/**
 * Build a stable Map<player_id, player> from the home + away squads. Call
 * once per (homeTeam, awayTeam) change and pass into formatDdsEventForTimeline
 * so each event lookup is O(1) instead of an O(n) array scan.
 */
export const createPlayerLookup = (homeTeam, awayTeam) => {
    const map = new Map();
    for (const p of homeTeam?.player || []) {
        if (p?.player_id) map.set(p.player_id, p);
    }
    for (const p of awayTeam?.player || []) {
        if (p?.player_id) map.set(p.player_id, p);
    }
    return map;
};

/**
 * Format a DDS event (from fixture.event) for the two-sided Events timeline.
 * Returns one entry per visual row (subs = 2 entries: sub_in, sub_out).
 * @param {Object} e - DDS event
 * @param {Map<string, Object>} playerLookup - from createPlayerLookup
 * @param {string} homeTeamId
 * @returns {Array<{ id: string, clockLabel: string, side: 'home'|'away', eventType: string, playerLabel: string, updatedAt?: string }>}
 */
export const formatDdsEventForTimeline = (e, playerLookup, homeTeamId) => {
    const baseId = e.event_id || `ev-${e.time_in_seconds}-${e.player1_id ?? ''}-${e.player2_id ?? ''}-${e.event_key ?? ''}`;
    const totalSeconds = Number(e.time_in_seconds) || 0;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const ss = String(seconds).padStart(2, '0');
    const clockLabel = minutes > 90 ? `90:00+${minutes - 90}:${ss}` : `${minutes}:${ss}`;

    // Event-context name priority: unique_name → short last → player_last_name
    const playerLabel = (p) => (p ? `${p.shirt_number ?? ''} - ${getEventName(p)}`.trim() : '');

    const side = e.team_id === homeTeamId ? 'home' : 'away';
    const p1 = e.player1_id ? playerLookup?.get(e.player1_id) ?? null : null;
    const p2 = e.player2_id ? playerLookup?.get(e.player2_id) ?? null : null;

    const key = (e.event_key || '').toLowerCase();
    const out = [];
    const updatedAt = e.updatedAt ?? e.updated_at;
    const timeInSeconds = Number(e.time_in_seconds) || 0;

    // Raw IDs carried through so EventEditModal can pre-populate player dropdowns.
    const player1_id = e.player1_id ?? null;
    const player2_id = e.player2_id ?? null;

    if (key === 'goal' || key === 'penalty' || key === 'own_goal') {
        out.push({
            id: baseId,
            clockLabel,
            side,
            eventType: 'goal',
            goalSubtype: key,
            playerLabel: p1 ? playerLabel(p1) : 'Goal',
            updatedAt,
            timeInSeconds,
            player1_id,
            player2_id,
        });
    } else if (key === 'yellow_card' || key === 'red_card' || key === 'double_yellow') {
        const eventType = key === 'red_card' ? 'red_card' : key === 'double_yellow' ? 'double_yellow' : 'yellow_card';
        out.push({
            id: baseId,
            clockLabel,
            side,
            eventType,
            playerLabel: p1 ? playerLabel(p1) : '',
            updatedAt,
            timeInSeconds,
            player1_id,
            player2_id,
        });
    } else if (key === 'sub' || key === 'concussion_sub') {
        // Sub: two rows — sub_in (player on) then sub_out (player off), same minute
        const onLabel = p2 ? playerLabel(p2) : '';
        const offLabel = p1 ? playerLabel(p1) : '';
        out.push(
            { id: `${baseId}-on`, clockLabel, side, eventType: 'sub_in', playerLabel: onLabel, updatedAt, timeInSeconds, player1_id, player2_id, isConcussion: key === 'concussion_sub' },
            { id: `${baseId}-off`, clockLabel, side, eventType: 'sub_out', playerLabel: offLabel, updatedAt, timeInSeconds, player1_id, player2_id, isConcussion: key === 'concussion_sub' }
        );
    } else if (PERIOD_LABELS[key]) {
        // Clock transition → centered divider. Every period marker (kick-off and
        // end-of-period) shows the real wall-clock time of day it was triggered,
        // parsed from the event_id epoch and rendered in the viewer's local
        // timezone. The end-of-period time inherently reflects added time played
        // (it is the actual moment the operator ended the half). Falls back to the
        // match clock if an id ever lacks an epoch suffix.
        const periodClock = eventIdToLocalTime(e.event_id) ?? clockLabel;
        out.push({
            id: baseId,
            clockLabel: periodClock,
            side,
            eventType: 'period',
            label: PERIOD_LABELS[key],
            updatedAt,
            timeInSeconds,
        });
    } else if (key === 'additional_time') {
        // Standalone "ADDITIONAL TIME N'" row logged when the operator announces
        // added time. The announced minutes ride in event_name (the schema has
        // no added_time column), so the persisted name is the display label.
        out.push({
            id: baseId,
            clockLabel,
            side,
            eventType: 'added_time',
            label: e.event_name || 'ADDITIONAL TIME',
            updatedAt,
            timeInSeconds,
        });
    } else {
        out.push({
            id: baseId,
            clockLabel,
            side,
            eventType: 'other',
            playerLabel: p1 ? playerLabel(p1) : (e.event_name || e.event_type || ''),
            updatedAt,
            timeInSeconds,
            player1_id,
            player2_id,
        });
    }

    return out;
};
